import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { onboardingStart, onboardingStatus, onboardingLatest } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, CheckCircle, Circle, Spinner, Warning, ArrowRight, Sparkle, Copy, Megaphone, MagnifyingGlass, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";

const GOALS = [
  ["brand_awareness", "Get known"],
  ["sales_growth", "Sell more"],
  ["lead_generation", "Get leads"],
  ["customer_engagement", "Build community"],
];

const StepRow = ({ s }) => (
  <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0" data-testid={`onboard-step-${s.key}`}>
    {s.status === "done" ? <CheckCircle size={20} className="text-emerald-400 shrink-0" weight="fill" />
      : s.status === "running" ? <Spinner size={20} className="brand-text shrink-0 animate-spin" />
        : s.status === "failed" ? <Warning size={20} className="text-amber-400 shrink-0" weight="fill" />
          : <Circle size={20} className="text-white/15 shrink-0" />}
    <div className="min-w-0">
      <div className={`text-sm font-semibold ${s.status === "pending" ? "text-[#5a5a5a]" : "text-white"}`}>{s.label}{s.status === "running" ? "…" : ""}</div>
      {s.detail && <div className={`text-xs mt-0.5 ${s.status === "failed" ? "text-amber-300/80" : "text-[#9a9a9a]"}`}>{s.detail}</div>}
    </div>
  </div>
);

function Results({ job, cid }) {
  const navigate = useNavigate();
  const r = job.results || {};
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const ad = r.extras?.ad_copy || {};
  return (
    <div className="space-y-5" data-testid="onboard-results">
      <div className="card-matte brand-glow p-7 text-center relative overflow-hidden">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 h-48 w-48 rounded-full bg-[#6B3DFF]/20 blur-[90px] pointer-events-none" />
        <Sparkle size={32} className="brand-text mx-auto mb-3" weight="fill" />
        <h2 className="font-head font-extrabold text-2xl md:text-3xl">Your AI marketing team is on the job.</h2>
        <p className="mt-2 text-sm text-[#9a9a9a] max-w-lg mx-auto">
          {r.calendar?.items_created ? <><b className="text-white">{r.calendar.items_created} pieces of content</b> are planned for {r.calendar.month} and waiting for your approval — nothing publishes without you.</> : "Setup complete — review the details below."}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button data-testid="onboard-goto-approvals" onClick={() => navigate("/approvals")} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-6 h-11">Review & Approve <ArrowRight size={15} className="ml-1.5" /></Button>
          <Button data-testid="onboard-goto-strategy" variant="ghost" onClick={() => navigate("/strategy")} className="border border-white/10 text-white rounded-full px-5 h-11">See My Content Strategy</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {r.website && (
          <div className="card-matte p-5" data-testid="onboard-result-website">
            <div className="label-caps mb-2 flex items-center gap-1.5"><MagnifyingGlass size={12} className="brand-text" /> Website analysis</div>
            <div className="font-head font-extrabold text-3xl">{r.website.overall}<span className="text-sm text-[#7a7a7a]">/100</span></div>
            <p className="text-xs text-[#9a9a9a] mt-1">{r.website.summary}</p>
            {(r.website.top_fixes || []).map((f, i) => <div key={i} className="text-xs text-[#cfcfcf] mt-1.5">→ {f}</div>)}
          </div>
        )}
        {r.industry && (
          <div className="card-matte p-5" data-testid="onboard-result-industry">
            <div className="label-caps mb-2">Industry & strategy</div>
            <div className="font-head font-bold text-lg brand-text">{r.industry.label}</div>
            <p className="text-xs text-[#9a9a9a] mt-1">{r.industry.reasoning}</p>
            <p className="text-xs text-[#cfcfcf] mt-2">Weekly content mix configured for Facebook & Instagram — tune it any time on the Content Strategy page.</p>
          </div>
        )}
        {(r.competitors || []).length > 0 && (
          <div className="card-matte p-5" data-testid="onboard-result-competitors">
            <div className="label-caps mb-2">Competitors on our radar</div>
            {r.competitors.map((c, i) => (
              <div key={i} className="text-xs mb-2"><b className="text-white">{c.name}</b><span className="text-[#9a9a9a]"> — {c.beat_them_angle || c.why}</span></div>
            ))}
          </div>
        )}
        {ad.headline && (
          <div className="card-matte p-5 relative" data-testid="onboard-result-ad">
            <button data-testid="onboard-ad-copy" onClick={() => copy(`${ad.headline}\n\n${ad.primary_text}\n\nCTA: ${ad.cta}`)}
              className="absolute top-3 right-3 flex items-center gap-1 text-[10px] text-[#9a9a9a] hover:text-white border border-white/10 rounded-full px-2.5 py-1"><Copy size={11} /> Copy</button>
            <div className="label-caps mb-2 flex items-center gap-1.5"><Megaphone size={12} className="brand-text" /> Your first ad, ready to run</div>
            <div className="font-semibold text-sm">{ad.headline}</div>
            <p className="text-xs text-[#9a9a9a] mt-1.5 whitespace-pre-wrap">{ad.primary_text}</p>
            <div className="text-xs brand-text mt-2 font-semibold">CTA: {ad.cta}</div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {(r.extras?.seo_recommendations || []).length > 0 && (
          <div className="card-matte p-5" data-testid="onboard-result-seo">
            <div className="label-caps mb-2">SEO recommendations</div>
            {r.extras.seo_recommendations.map((s, i) => <div key={i} className="text-xs text-[#cfcfcf] mb-1.5 flex gap-1.5"><span className="brand-text">{i + 1}.</span> {s}</div>)}
          </div>
        )}
        {(r.extras?.ai_recommendations || []).length > 0 && (
          <div className="card-matte p-5" data-testid="onboard-result-recs">
            <div className="label-caps mb-2 flex items-center gap-1.5"><Lightbulb size={12} className="text-amber-300" /> Your next best moves</div>
            {r.extras.ai_recommendations.map((s, i) => <div key={i} className="text-xs text-[#cfcfcf] mb-1.5 flex gap-1.5"><span className="text-amber-300">→</span> {s}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OnboardingPage() {
  const { activeId, activeClient } = useClient();
  const [website, setWebsite] = useState("");
  const [offering, setOffering] = useState("");
  const [goal, setGoal] = useState("brand_awareness");
  const [jobId, setJobId] = useState(null);

  const { data: latest } = useQuery({ queryKey: ["onboard-latest", activeId], queryFn: () => onboardingLatest(activeId), enabled: !!activeId && !jobId });
  const { data: job } = useQuery({
    queryKey: ["onboard-job", jobId],
    queryFn: () => onboardingStatus(jobId), enabled: !!jobId,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 3000 : false),
  });
  useEffect(() => { if (latest?.id && !jobId) setJobId(latest.id); }, [latest, jobId]);

  const start = useMutation({
    mutationFn: () => onboardingStart({ client_id: activeId, website, offering, goal }),
    onSuccess: (r) => { setJobId(r.job_id); toast.success("Your AI team just clocked in — watch it work"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start onboarding"),
  });

  const active = job?.id ? job : null;

  return (
    <PageContainer>
      <PageHeader icon={UserPlus} eyebrow="AI Command" title="AI Onboarding"
        subtitle="Three questions. Then your new AI marketing employee reads your website, learns your brand, sizes up competitors and plans your entire first month — while you watch." />

      {!active && (
        <div className="card-matte p-6 max-w-2xl" data-testid="onboard-form">
          <div className="label-caps mb-1">Your website <span className="text-[#5a5a5a] normal-case">(optional but recommended)</span></div>
          <Input data-testid="onboard-website" value={website} onChange={(e) => setWebsite(e.target.value)}
            placeholder={activeClient?.website || "https://yourbusiness.com"} className="bg-[#10101A] border-white/10 h-11 mb-4" />
          <div className="label-caps mb-1">What do you sell, in one line?</div>
          <Input data-testid="onboard-offering" value={offering} onChange={(e) => setOffering(e.target.value)}
            placeholder="e.g. Specialty coffee, fresh bakes and a place people love" className="bg-[#10101A] border-white/10 h-11 mb-4" />
          <div className="label-caps mb-2">What matters most right now?</div>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="onboard-goals">
            {GOALS.map(([k, l]) => (
              <button key={k} data-testid={`onboard-goal-${k}`} onClick={() => setGoal(k)}
                className={`h-10 rounded-full px-4 text-sm font-semibold border transition-colors ${goal === k ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <Button data-testid="onboard-start-btn" onClick={() => start.mutate()} disabled={start.isPending || !activeId}
            className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-12 text-base">
            <Sparkle size={17} className="mr-2" weight="fill" /> {start.isPending ? "Clocking in…" : "Hire My AI Marketing Team"}
          </Button>
          <p className="mt-3 text-[10px] text-[#5a5a5a] text-center">Takes ~3-5 minutes. Everything lands in your Approval Queue — nothing publishes without you.</p>
        </div>
      )}

      {active && active.status === "running" && (
        <div className="card-matte p-6 max-w-2xl" data-testid="onboard-progress">
          <div className="font-head font-semibold mb-1">Day one on the job — here's what I'm doing:</div>
          <div className="text-xs text-[#9a9a9a] mb-3">You can leave this page — I'll keep working in the background.</div>
          {(active.steps || []).map((s) => <StepRow key={s.key} s={s} />)}
        </div>
      )}

      {active && active.status === "done" && (
        <>
          <Results job={active} cid={activeId} />
          <button data-testid="onboard-run-again" onClick={() => setJobId(null)}
            className="mt-5 text-[11px] text-[#7a7a7a] hover:text-white underline underline-offset-2">Run onboarding again</button>
        </>
      )}
    </PageContainer>
  );
}
